import React, { useCallback } from 'react';
import { useTheme as useMuiTheme, alpha } from '@mui/material';
import { useTheme } from '../../../contexts/ThemeContext';

/**
 * Type definitions used across extension components for more consistent behavior
 */

/**
 * Common interface for cost related display properties
 * This helps ensure consistent behavior across different components
 */
export interface CostDisplayProperties {
  // Unit cost override flag
  isoverrideitemunitcost?: boolean;
  // Unit cost value
  itemunitcost?: number;
  // Production rate override flag
  isoverridehourlyproductionrate?: boolean;
  // Production rate value
  hourlyproductionrate?: number;
  // Is quoted flag
  isquoted?: boolean;
  // Unit of measure value factor
  itemuomvalue?: number;
}

/**
 * Helper function to calculate total material cost with UOM consideration
 */
export const calculateTotalMaterialCost = (
  unitCost: number,
  totalQty: number,
  uomValue: number = 1
): number => {
  if (uomValue <= 0) uomValue = 1;
  // Perform calculation with full precision
  const result = (unitCost / uomValue) * totalQty;
  // Return full precision result
  return result;
};

/**
 * Helper function to calculate total labor hours with UOM consideration
 */
export const calculateTotalLaborHours = (
  productionRate: number,
  totalQty: number,
  uomValue: number = 1
): number => {
  if (productionRate <= 0) return 0;
  if (uomValue <= 0) uomValue = 1;
  // Perform calculation with full precision
  const result = (productionRate / uomValue) * totalQty;
  // Return full precision result
  return result;
};

/**
 * Determines the background color of a field based on its properties
 */
export const useFieldBackground = () => {
  const muiTheme = useMuiTheme();
  const { darkMode } = useTheme();

  return useCallback(
    (item: CostDisplayProperties) => {
      // Check for the different states in order of precedence
      if (item.isquoted) {
        // Quoted items - use neutral gray
        return darkMode
          ? alpha(muiTheme.palette.grey[700], 0.7)
          : alpha(muiTheme.palette.grey[300], 0.9);
      }

      if (item.isoverrideitemunitcost || item.isoverridehourlyproductionrate) {
        // Override values - use warning color (yellow-ish)
        return darkMode
          ? alpha(muiTheme.palette.warning.dark, 0.2)
          : alpha(muiTheme.palette.warning.light, 0.2);
      }

      if (
        (item.itemunitcost === 0 && 'itemunitcost' in item) ||
        (item.hourlyproductionrate === 0 && 'hourlyproductionrate' in item)
      ) {
        // Zero values - use error color (red-ish)
        return darkMode
          ? alpha(muiTheme.palette.error.dark, 0.15)
          : alpha(muiTheme.palette.error.light, 0.15);
      }

      // Default values - use success color (green-ish)
      return darkMode
        ? alpha(muiTheme.palette.success.dark, 0.15)
        : alpha(muiTheme.palette.success.light, 0.15);
    },
    [muiTheme, darkMode]
  );
};

/**
 * Determines the text color of a field based on its properties
 */
export const useFieldTextColor = () => {
  const muiTheme = useMuiTheme();
  const { darkMode } = useTheme();

  return useCallback(
    (item: CostDisplayProperties) => {
      // Check for the different states in order of precedence
      if (item.isquoted) {
        // Quoted items - use muted text
        return darkMode
          ? alpha(muiTheme.palette.text.secondary, 0.9)
          : muiTheme.palette.text.secondary;
      }

      if (item.isoverrideitemunitcost || item.isoverridehourlyproductionrate) {
        // Override values - use warning color
        return darkMode ? muiTheme.palette.warning.light : muiTheme.palette.warning.dark;
      }

      if (
        (item.itemunitcost === 0 && 'itemunitcost' in item) ||
        (item.hourlyproductionrate === 0 && 'hourlyproductionrate' in item)
      ) {
        // Zero values - use error color
        return darkMode ? muiTheme.palette.error.light : muiTheme.palette.error.dark;
      }

      // Default values - use success color
      return darkMode ? muiTheme.palette.success.light : muiTheme.palette.success.dark;
    },
    [muiTheme, darkMode]
  );
};

/**
 * Returns styling for active cells in the table
 */
export const useActiveCellStyle = () => {
  const muiTheme = useMuiTheme();
  const { darkMode } = useTheme();

  return useCallback(
    (isActiveRow: boolean, activeCellIndex: number | null, cellIndex: number) => {
      if (isActiveRow && activeCellIndex === cellIndex) {
        return {
          boxShadow: `0 0 0 2px ${muiTheme.palette.primary.main}, 0 0 0 4px ${alpha(muiTheme.palette.primary.main, 0.3)}`,
          border: `1px solid ${muiTheme.palette.primary.main}`,
          backgroundColor: alpha(muiTheme.palette.primary.main, darkMode ? 0.25 : 0.15),
          // Add a pulsing animation to make it more visible
          animation: 'pulse 2s infinite',
          '@keyframes pulse': {
            '0%': {
              boxShadow: `0 0 0 2px ${muiTheme.palette.primary.main}, 0 0 0 4px ${alpha(muiTheme.palette.primary.main, 0.3)}`,
            },
            '50%': {
              boxShadow: `0 0 0 2px ${muiTheme.palette.primary.main}, 0 0 0 6px ${alpha(muiTheme.palette.primary.main, 0.2)}`,
            },
            '100%': {
              boxShadow: `0 0 0 2px ${muiTheme.palette.primary.main}, 0 0 0 4px ${alpha(muiTheme.palette.primary.main, 0.3)}`,
            },
          },
        };
      }
      return {};
    },
    [muiTheme, darkMode]
  );
};

/**
 * Returns styling for column highlighting
 */
export const useColumnHighlightStyle = () => {
  const muiTheme = useMuiTheme();
  const { darkMode } = useTheme();

  return useCallback(
    (selectedColumnIndex: number | null, columnIndex: number) => {
      if (selectedColumnIndex === columnIndex) {
        return {
          backgroundColor: alpha(muiTheme.palette.primary.main, darkMode ? 0.2 : 0.1),
          borderLeft: `2px solid ${muiTheme.palette.primary.main}`,
          borderRight: `2px solid ${muiTheme.palette.primary.main}`,
        };
      }
      return {};
    },
    [muiTheme, darkMode]
  );
};

/**
 * Hook to manage update queue for batched updates
 */
export const useUpdateQueue = () => {
  const [isProcessingQueue, setIsProcessingQueue] = React.useState(false);
  const updateQueue = React.useRef<Array<() => void>>([]);

  const processUpdateQueue = useCallback(() => {
    if (updateQueue.current.length === 0) {
      setIsProcessingQueue(false);
      return;
    }

    setIsProcessingQueue(true);
    const update = updateQueue.current.shift();
    if (update) {
      // Execute the update
      update();

      // Schedule the next update with a small delay to allow UI to breathe
      setTimeout(processUpdateQueue, 50);
    } else {
      setIsProcessingQueue(false);
    }
  }, []);

  const queueUpdate = useCallback(
    (updateFn: () => void) => {
      updateQueue.current.push(updateFn);

      if (!isProcessingQueue) {
        processUpdateQueue();
      }
    },
    [isProcessingQueue, processUpdateQueue]
  );

  return queueUpdate;
};
